<template>
    <div>
        <div class="flex gap-4">
            <div v-if="isMinFixed" class="grid w-full max-w-sm gap-1">
                <Label for="pastMonths">{{
                    $t("configurator.closest_bookable_day")
                }}</Label>

                <InputWithSelect v-model="min" />
            </div>
            <div class="grid w-full max-w-sm gap-1">
                <Label for="pastMonths">{{
                    $t("configurator.max_advance_booking")
                }}</Label>
                <InputWithSelect v-model="max" />
            </div>
        </div>
        <div
            class="mt-8 rounded-lg border p-4"
            :class="[
                enabled
                    ? 'border-z-primary-500 bg-z-primary-25'
                    : 'border-iron-200',
            ]"
        >
            <div class="flex gap-2.5">
                <Switch v-model:checked="enabled" class="mt-1" />
                <div>
                    <p class="text-sm font-medium text-iron-700">
                        {{ $t("event_types.limits.booking_frequency") }}
                    </p>
                    <p class="text-sm text-iron-500">
                        {{
                            $t(
                                "event_types.limits.booking_frequency_description",
                            )
                        }}
                    </p>
                </div>
            </div>

            <div v-if="enabled" v-auto-animate class="mt-4 pl-[54px]">
                <div
                    v-for="frequency in activeFrequencies"
                    :key="frequency"
                    v-auto-animate
                    class="grid grid-cols-[146px_146px_40px] gap-2 py-2"
                >
                    <Input
                        v-model="frequenceiesState[frequency]"
                        :min="1"
                        type="number"
                    />

                    <Select
                        :model-value="frequency"
                        @update:model-value="
                            (val) => update(val as FrequencyOptions, frequency)
                        "
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="" />
                        </SelectTrigger>
                        <SelectContent>
                            <template
                                v-for="option of [...optionsLeft, frequency]"
                                :key="option"
                            >
                                <SelectGroup>
                                    <SelectItem :value="option">
                                        {{ $t("event_types.limits." + option) }}
                                    </SelectItem>
                                </SelectGroup>
                            </template>
                        </SelectContent>
                    </Select>

                    <div class="flex items-center justify-center">
                        <Button
                            v-if="activeFrequencies.length > 1"
                            class=""
                            variant="ghost"
                            @click="deleteFrequency(frequency)"
                        >
                            <Trash class="text-iron-500" :size="20"></Trash>
                        </Button>
                    </div>
                </div>

                <div class="grid grid-cols-[146px_146px_40px] gap-2">
                    <div>
                        <Button
                            v-if="optionsLeft.length > 0"
                            size="sm"
                            class="mt-2 text-z-primary-600"
                            variant="ghost"
                            @click="addLimit"
                        >
                            <Plus :size="20" class="text-z-primary-500" />
                            {{ $t("event_types.limits.add_limit") }}
                        </Button>
                    </div>

                    <div class="justify-self-end">
                        <Transition>
                            <Button
                                v-if="saveable"
                                v-auto-animate
                                size="sm"
                                class="mt-2"
                                @click="saveFrequency"
                                >{{ $t("save") }}</Button
                            >
                        </Transition>
                    </div>

                    <div></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ref, watch, reactive, onMounted } from "vue";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, Trash } from "lucide-vue-next";
import { vAutoAnimate } from "@formkit/auto-animate";
import { EventType } from "@/Pages/ZapTime/App/EventTypes/types/EventType";
import { EventTypeUser } from "@/Pages/ZapTime/App/EventTypes/types/EventTypeUser";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import InputWithSelect from "@/components/ui/input-with-select/InputWithSelect.vue";
import { debouncedWatch } from "@vueuse/core";
import { Label } from "@/components/ui/label";
import { useEventTypeStore } from "../stores/eventTypeStore";
const props = defineProps<{
    eventType: EventType;
    eventTypeUser: EventTypeUser;
}>();

console.log(props.eventType);
const frequencyOptionsType = [
    "per-day",
    "per-week",
    "per-month",
    "per-year",
] as const;

const initialFrequencies = ref<FrequencyOptions[]>([]);

type FrequencyOptions = (typeof frequencyOptionsType)[number];

const frequenceiesState = reactive<
    Record<FrequencyOptions, undefined | number>
>({
    "per-day": undefined,
    "per-week": undefined,
    "per-month": undefined,
    "per-year": undefined,
});

const optionsLeft = ref<FrequencyOptions[]>([
    "per-day",
    "per-week",
    "per-month",
    "per-year",
]);

console.log(props.eventTypeUser.booking_rules);
const eventTypesStore = useEventTypeStore();

const min = ref(
    props.eventTypeUser.booking_rules?.minimum_booking_notice ?? null,
);

const isMinFixed = ref(false);
const max = ref(props.eventTypeUser.booking_rules?.max_advance_booking ?? null);

const activeFrequencies = ref<FrequencyOptions[]>([]);

const enabled = ref(
    props.eventTypeUser.booking_rules?.limit_booking_frequency?.enabled ??
        false,
);

if (props.eventTypeUser.booking_rules?.limit_booking_frequency?.["per-day"]) {
    initialFrequencies.value.push("per-day");

    frequenceiesState["per-day"] =
        props.eventTypeUser.booking_rules?.limit_booking_frequency["per-day"];

    optionsLeft.value = optionsLeft.value.filter(
        (option) => option !== "per-day",
    );

    activeFrequencies.value.push("per-day");
}

if (props.eventTypeUser.booking_rules?.limit_booking_frequency?.["per-week"]) {
    initialFrequencies.value.push("per-week");

    frequenceiesState["per-week"] =
        props.eventTypeUser.booking_rules?.limit_booking_frequency["per-week"];

    optionsLeft.value = optionsLeft.value.filter(
        (option) => option !== "per-week",
    );

    activeFrequencies.value.push("per-week");
}

if (props.eventTypeUser.booking_rules?.limit_booking_frequency?.["per-month"]) {
    initialFrequencies.value.push("per-month");

    frequenceiesState["per-month"] =
        props.eventTypeUser.booking_rules?.limit_booking_frequency["per-month"];

    optionsLeft.value = optionsLeft.value.filter(
        (option) => option !== "per-month",
    );

    activeFrequencies.value.push("per-month");
}

if (props.eventTypeUser.booking_rules?.limit_booking_frequency?.["per-year"]) {
    initialFrequencies.value.push("per-year");

    frequenceiesState["per-year"] =
        props.eventTypeUser.booking_rules?.limit_booking_frequency["per-year"];

    optionsLeft.value = optionsLeft.value.filter(
        (option) => option !== "per-year",
    );

    activeFrequencies.value.push("per-year");
}

const saveable = ref(false);

watch(enabled, () => {
    saveFrequency();
});

watch([frequenceiesState, enabled], () => {
    saveable.value = true;
});

debouncedWatch(
    [min, max],
    () => {
        saveFrequency();
    },
    {
        debounce: 500,
    },
);

function saveFrequency() {
    router.put(
        route("app.event-types.booking-rules.update", {
            eventType: props.eventType.id,
        }),
        {
            limit_booking_frequency: {
                enabled: enabled.value,
                "per-day": frequenceiesState["per-day"],
                "per-week": frequenceiesState["per-week"],
                "per-month": frequenceiesState["per-month"],
                "per-year": frequenceiesState["per-year"],
            },
            max_advance_booking: max.value, // week
            minimum_booking_notice: min.value,
        },
    );

    saveable.value = false;
}

function update(newFreq: FrequencyOptions, oldFreq: FrequencyOptions) {
    if (newFreq === oldFreq) return;
    frequenceiesState[newFreq] = frequenceiesState[oldFreq];
    frequenceiesState[oldFreq] = undefined;

    activeFrequencies.value = activeFrequencies.value.filter(
        (freq) => freq !== oldFreq,
    );

    pushToActiveFrequnciesAndSort(newFreq);

    optionsLeft.value.push(oldFreq);
    optionsLeft.value = optionsLeft.value.filter(
        (option) => option !== newFreq,
    );
}

function deleteFrequency(frequency: FrequencyOptions) {
    frequenceiesState[frequency] = undefined;

    optionsLeft.value.push(frequency);
    activeFrequencies.value = activeFrequencies.value.filter(
        (activeFrequency) => activeFrequency !== frequency,
    );
}

function addLimit() {
    if (!activeFrequencies.value.includes("per-day")) {
        pushToActiveFrequnciesAndSort("per-day");

        frequenceiesState["per-day"] = 1;

        optionsLeft.value = optionsLeft.value.filter(
            (option) => option !== "per-day",
        );
        return;
    }

    if (!activeFrequencies.value.includes("per-week")) {
        pushToActiveFrequnciesAndSort("per-week");
        frequenceiesState["per-week"] = 1;

        optionsLeft.value = optionsLeft.value.filter(
            (option) => option !== "per-week",
        );
        return;
    }

    if (!activeFrequencies.value.includes("per-month")) {
        pushToActiveFrequnciesAndSort("per-month");
        frequenceiesState["per-month"] = 1;

        optionsLeft.value = optionsLeft.value.filter(
            (option) => option !== "per-month",
        );
        return;
    }

    if (!activeFrequencies.value.includes("per-year")) {
        pushToActiveFrequnciesAndSort("per-year");
        frequenceiesState["per-year"] = 1;

        optionsLeft.value = optionsLeft.value.filter(
            (option) => option !== "per-year",
        );
        return;
    }
}

function frequnciesComporator(a: FrequencyOptions, b: FrequencyOptions) {
    if (a === "per-day") return -1;
    if (a === "per-year") return 1;
    if (a === "per-week" && b === "per-day") return 1;
    if (a === "per-week" && b === "per-month") return -1;
    if (a === "per-week" && b === "per-year") return -1;
    if (a === "per-month" && b === "per-year") return -1;

    return 1;
}

function pushToActiveFrequnciesAndSort(frequency: FrequencyOptions) {
    activeFrequencies.value.push(frequency);

    activeFrequencies.value =
        activeFrequencies.value.toSorted(frequnciesComporator);
}

onMounted(() => {
    if (
        props.eventType.configuration.closestBookableDay !== undefined &&
        props.eventType.configuration.closestBookableDay !== 0 &&
        min.value == null
    ) {
        min.value = props.eventType.configuration.closestBookableDay * 86400;
        saveFrequency();
        eventTypesStore.closestBookableDay = 0;
    }
    isMinFixed.value = true;
});
</script>
